import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContextType';
import { getEmbeddingsOverview, type EmbeddingsOverview } from '../services/adminEmbeddingsService';
import PageHeader from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';

const num = (n: number | null | undefined) => (n === null || n === undefined ? '—' : n.toLocaleString());
const ts = (s: string | null | undefined) => (s ? new Date(s).toLocaleString() : '—');

const Stat = ({ label, value, mono = false }: { label: string; value: string | number; mono?: boolean }) => (
  <span className="text-xs text-slate-500">
    {label}: <span className={`font-semibold text-slate-700 ${mono ? 'font-mono' : ''}`}>{value}</span>
  </span>
);

const Th = ({ children, right = false }: { children: React.ReactNode; right?: boolean }) => (
  <th className={`${right ? 'text-right' : 'text-left'} py-1 px-2 font-medium text-slate-500`}>{children}</th>
);

const Td = ({ children, right = false, className = '' }: { children: React.ReactNode; right?: boolean; className?: string }) => (
  <td className={`py-1 px-2 ${right ? 'text-right tabular-nums' : ''} text-slate-600 ${className}`}>{children}</td>
);

const SectionTitle = ({ children }: { children: React.ReactNode }) => (
  <h2 className="text-sm font-semibold text-slate-700 pb-1 border-b border-slate-200">{children}</h2>
);

export default function SettingsEmbeddings() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isSysAdmin = user && ['SysAdmin', 'DevOps'].includes(user.role ?? '');

  const [data, setData] = useState<EmbeddingsOverview | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      setData(await getEmbeddingsOverview());
    } catch (err: unknown) {
      const e = err as { response?: { data?: { detail?: string } } };
      setError(e?.response?.data?.detail ?? t('pages.settingsEmbeddings.errorLoad'));
    } finally { setLoading(false); }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  if (!isSysAdmin) return null;

  const p = 'pages.settingsEmbeddings';
  const model = data?.embedding_model;
  const sweep = data?.sweep;
  const corpus = data?.corpus;
  const consistency = data?.qdrant_consistency;

  const sweepTone = sweep?.running
    ? (sweep.last_tick_error ? 'bg-red-50 border-red-200' : sweep.backoff_active ? 'bg-amber-50 border-amber-200' : 'bg-green-50 border-green-200')
    : sweep?.enabled ? 'bg-amber-50 border-amber-200' : 'bg-slate-50 border-slate-200';

  return (
    <>
      <PageHeader title={t(`${p}.title`)} subtitle={t(`${p}.subtitle`)} />

      <div className="px-6 pb-6 space-y-5 pt-4">
        <div className="flex items-center gap-3 flex-wrap">
          {data && (
            <div className="flex gap-4 text-xs text-slate-500 flex-1">
              <Stat label={t(`${p}.generatedAt`)} value={ts(data.generated_at)} />
            </div>
          )}
          <Button size="sm" variant="outline" onClick={load} disabled={loading}>
            {loading ? t(`${p}.loading`) : t(`${p}.btnRefresh`)}
          </Button>
        </div>

        {error && <p className="text-sm text-red-600">{error}</p>}

        {model && (
          <section className="space-y-2">
            <SectionTitle>{t(`${p}.sectionModel`)}</SectionTitle>
            <div className="flex gap-4 flex-wrap items-center">
              <Stat label={t(`${p}.provider`)} value={model.provider} mono />
              <Stat label={t(`${p}.model`)} value={model.model_tag ?? model.model_id ?? '—'} mono />
              <Stat label={t(`${p}.dimensions`)} value={model.dimensions ?? '—'} />
              <Stat label={t(`${p}.distance`)} value={model.distance ?? '—'} mono />
              <Stat label={t(`${p}.vectorBackend`)} value={model.vector_backend} mono />
              <span className={`text-xs font-semibold ${model.available ? 'text-green-700' : 'text-red-600'}`}>
                {model.available ? t(`${p}.providerAvailable`) : t(`${p}.providerUnavailable`)}
              </span>
            </div>
            {model.error && <p className="text-xs text-red-600">{model.error}</p>}
          </section>
        )}

        {sweep && (
          <>
            <Separator />
            <section className="space-y-2">
              <SectionTitle>{t(`${p}.sectionSweep`)}</SectionTitle>
              <div className={`border rounded px-3 py-2 space-y-1 ${sweepTone}`}>
                <div className="flex gap-4 flex-wrap items-center">
                  <span className="text-xs font-semibold text-slate-700">
                    {!sweep.enabled ? t(`${p}.sweepDisabled`)
                      : !sweep.running ? t(`${p}.sweepNotRunning`)
                      : sweep.last_tick_finished_at ? t(`${p}.sweepRunning`)
                      : t(`${p}.sweepNoTickYet`)}
                  </span>
                  <Stat label={t(`${p}.interval`)} value={`${sweep.interval_seconds}s`} />
                  <Stat label={t(`${p}.maxDocsPerTick`)} value={num(sweep.max_docs_per_kb)} />
                  <Stat label={t(`${p}.backlog`)} value={num(sweep.backlog_not_embedded)} />
                </div>
                {sweep.enabled && (
                  <div className="flex gap-4 flex-wrap">
                    <Stat label={t(`${p}.lastTickFinished`)} value={ts(sweep.last_tick_finished_at)} />
                    <Stat label={t(`${p}.lastTickEmbedded`)} value={num(sweep.last_tick_embedded)} />
                    <Stat label={t(`${p}.ticksCompleted`)} value={num(sweep.ticks_completed)} />
                  </div>
                )}
                {sweep.backoff_active && (
                  <p className="text-xs text-amber-700">
                    {t(`${p}.backoffActive`, { failures: sweep.consecutive_failures, seconds: sweep.next_tick_in_seconds, max: sweep.backoff_max_seconds })}
                  </p>
                )}
                {sweep.last_tick_error && (
                  <p className="text-xs text-red-600">{t(`${p}.lastTickError`)}: {sweep.last_tick_error}</p>
                )}
              </div>
            </section>
          </>
        )}

        {data && (
          <>
            <Separator />
            <section className="space-y-2">
              <SectionTitle>{t(`${p}.sectionLkbs`)}</SectionTitle>
              {data.lkbs.length === 0 ? (
                <p className="text-xs text-slate-500">{t(`${p}.noLkbs`)}</p>
              ) : (
                <>
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-slate-100">
                        <Th>{t(`${p}.colName`)}</Th>
                        <Th right>{t(`${p}.colDocs`)}</Th>
                        <Th right>{t(`${p}.colDone`)}</Th>
                        <Th right>{t(`${p}.colNotEmbedded`)}</Th>
                        <Th>{t(`${p}.colJobStatus`)}</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.lkbs.map(lkb => (
                        <tr key={lkb.kb_id} className="border-b border-slate-50 hover:bg-slate-50">
                          <Td className="font-medium text-slate-700">{lkb.name ?? lkb.kb_id}</Td>
                          <Td right>{num(lkb.document_count)}</Td>
                          <Td right className="text-green-700 font-semibold">{num(lkb.job.processed_documents)}</Td>
                          <Td right className={lkb.not_embedded > 0 ? 'text-amber-700 font-semibold' : ''}>{num(lkb.not_embedded)}</Td>
                          <Td>
                            <span className="font-mono">{lkb.job.status}</span>
                            {lkb.job.stalled && <span className="text-amber-700 ml-1">({t(`${p}.stalled`)})</span>}
                            {lkb.job.error_message && <span className="text-red-600 ml-1">{lkb.job.error_message}</span>}
                          </Td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <p className="text-xs text-slate-400">{t(`${p}.doneAsOf`)}</p>
                </>
              )}
            </section>

            <Separator />
            <section className="space-y-2">
              <SectionTitle>{t(`${p}.sectionCorpus`)}</SectionTitle>
              <div className="flex gap-4 flex-wrap">
                <Stat label={t(`${p}.totalDocs`)} value={num(corpus?.estimated_document_count ?? 0)} />
                {corpus && Object.entries(corpus.status_counts).map(([status, count]) => (
                  <Stat key={status} label={status} value={num(count)} mono />
                ))}
              </div>
              <p className="text-xs text-slate-500">
                {t(`${p}.unassignedPending`)}: <span className="font-semibold text-slate-700">{num(corpus?.unassigned_pending ?? 0)}</span>
                {' — '}{t(`${p}.unassignedExplain`)}
              </p>
              {corpus && corpus.failed_sample.length > 0 && (
                <p className="text-xs text-red-600">
                  {t(`${p}.failedSample`)}: {corpus.failed_sample.map(d => d.patent_id || d.title || d.id).join(', ')}
                </p>
              )}
            </section>

            <Separator />
            <section className="space-y-2">
              <SectionTitle>{t(`${p}.sectionSkbs`)}</SectionTitle>
              {data.skbs.length === 0 ? (
                <p className="text-xs text-slate-500">{t(`${p}.noSkbs`)}</p>
              ) : (
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-slate-100">
                      <Th>{t(`${p}.colName`)}</Th>
                      <Th>{t(`${p}.colVisibility`)}</Th>
                      <Th right>{t(`${p}.colDocs`)}</Th>
                      <Th right>{t(`${p}.colPrepared`)}</Th>
                      <Th right>{t(`${p}.colChunks`)}</Th>
                      <Th>{t(`${p}.colIndexStatus`)}</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.skbs.map(skb => (
                      <tr key={skb.kb_id} className="border-b border-slate-50 hover:bg-slate-50">
                        <Td className="font-medium text-slate-700">{skb.name ?? skb.kb_id}</Td>
                        <Td><span className="font-mono">{skb.visibility ?? '—'}</span></Td>
                        <Td right>{num(skb.document_count)}</Td>
                        <Td right>{num(skb.prepared_document_count)}</Td>
                        <Td right>{num(skb.chunk_count)}</Td>
                        <Td>
                          {skb.vector_index?.status
                            ? <span className="font-mono">{skb.vector_index.status}{skb.vector_index.store ? ` (${skb.vector_index.store})` : ''}</span>
                            : <span className="text-slate-400">{t(`${p}.notPrepared`)}</span>}
                          {skb.vector_index?.error && <span className="text-red-600 ml-1">{skb.vector_index.error}</span>}
                        </Td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </section>

            <Separator />
            <section className="space-y-2">
              <SectionTitle>{t(`${p}.sectionConsistency`)}</SectionTitle>
              {!consistency?.available ? (
                <div className="border border-red-200 bg-red-50 rounded px-3 py-2">
                  <p className="text-xs text-red-700 font-medium">{t(`${p}.consistencyUnavailable`)}</p>
                  {consistency?.error && <p className="text-xs text-red-600 mt-1 font-mono">{consistency.error}</p>}
                </div>
              ) : consistency.collections.length === 0 ? (
                <p className="text-xs text-slate-500">{t(`${p}.noCollections`)}</p>
              ) : (
                <>
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-slate-100">
                        <Th>{t(`${p}.colCollection`)}</Th>
                        <Th>{t(`${p}.colKind`)}</Th>
                        <Th right>{t(`${p}.colQdrantPoints`)}</Th>
                        <Th right>{t(`${p}.colExpected`)}</Th>
                        <Th right>{t(`${p}.colDelta`)}</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {consistency.collections.map(row => (
                        <tr key={row.collection} className="border-b border-slate-50 hover:bg-slate-50">
                          <Td className="font-mono break-all">{row.collection}</Td>
                          <Td>
                            <span className="font-mono">{row.kind}</span>
                            {row.note_key === 'aggregate' && <span className="text-slate-400 ml-1">({t(`${p}.aggregateNote`)})</span>}
                          </Td>
                          <Td right>{row.error ? <span className="text-red-600">{t(`${p}.collectionNotFound`)}</span> : num(row.qdrant_points)}</Td>
                          <Td right>{num(row.mongo_expected)}</Td>
                          <Td right className={row.delta === 0 ? 'text-green-700' : 'text-red-600 font-semibold'}>
                            {row.delta === null ? '—' : num(row.delta)}
                          </Td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <p className="text-xs text-slate-400">{t(`${p}.consistencyHint`)}</p>
                </>
              )}
            </section>
          </>
        )}
      </div>
    </>
  );
}
